// src/components/ExamSpreadsheetGrid.jsx
// An editable grid (rows x columns of cells) for exam-mode computational/
// workings answers, styled and behaving like a real spreadsheet (Excel/
// Google Sheets): resizable columns, click-and-drag multi-cell range
// selection, range copy/paste, Tab/Enter keyboard navigation, basic formulas
// (see spreadsheetFormulas.js), and per-cell text formatting (bold/italic/
// underline/align), cell borders and number formats applied via a toolbar
// to whatever is currently selected, plus the other tools ACCA's own exam
// spreadsheet offers: format painter, fill handle, currency symbols, and
// copy/paste within the grid that keeps formatting and adjusts formula
// references like Excel.
//
// Content is serialized to a single JSON string (gridJson) so it stays one
// scalar Firestore field: { grid: [[cell,...],...], colWidths: [n,...] }.
// Legacy documents saved before colWidths existed (a bare array of rows) are
// still read correctly - see parseGrid.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Baseline, PaintBucket, Scissors, Copy, ClipboardPaste, Ban, Grid2x2, Square, PanelBottom, PanelTop, PanelLeft, PanelRight, Rows2, Equal, Minus, ChevronDown, Hash, DollarSign, PoundSterling, Euro, Percent, DecimalsArrowLeft, DecimalsArrowRight, Paintbrush, Coins } from 'lucide-react';
import { evaluateGrid, shiftFormula, visibleDecimals } from '../utils/spreadsheetFormulas';
import { rememberExamCopy, isExamCopy, PASTE_BLOCKED_MESSAGE } from '../utils/examClipboard';

const IS_MAC = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
const CONTEXT_MENU_WIDTH = 170;
const CONTEXT_MENU_HEIGHT = 100;

// Drops any text selected inside a cell's input, leaving the caret in place
const collapseTextSelection = (el) => {
  if (el && el.tagName === 'INPUT' && el.selectionStart !== el.selectionEnd) el.setSelectionRange(el.selectionEnd, el.selectionEnd);
};

const DEFAULT_ROWS = 30;
const DEFAULT_COLS = 8;
const DEFAULT_CELL_WIDTH = 64; // starting column width in px - Excel's default, and close to ACCA's iAssess (~62px)
const MIN_COL_WIDTH = 40;
// The input inside each cell is position:absolute (so it can overflow into a
// neighbouring cell) - that takes it out of normal flow, so the <td> has
// nothing left to size its own height from and collapses. Give rows an
// explicit height to compensate.
const ROW_HEIGHT = 20; // Excel's/iAssess's default row height; rows with a larger font grow to fit (rowHeightOf)
// Sheet size cap: 200 rows, columns A to Z - comfortably more than any ACCA
// spreadsheet answer needs (longest: FR/SBR consolidations, widest: FM/AFM
// year-by-year NPVs). iAssess offers 300 x CV, but every cell here is a real
// input re-rendered on each keystroke, and at ~20,000 cells typing took
// seconds per key (tested 2026-10-31), so the cap is kept to what students
// actually use. All 26 columns are there from the start; rows are added as
// the student scrolls or moves towards the bottom (see ensureFilled).
const MAX_ROWS = 200;
const MAX_COLS = 26;
const GROW_ROWS = 20;
const GROW_MARGIN_PX = 300; // add rows once the visible area gets this close to the last one
const ROW_HEADER_WIDTH = 36; // the "1, 2, 3..." gutter column, matches the real exam software's proportions
const SELECTION_COLOR = '#1a73e8'; // the blue Excel/Sheets both use for the active selection
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;

// A1-style column letters: 0 -> A, 25 -> Z, 26 -> AA, etc.
// Exported for reuse by SpreadsheetCellViewer.jsx (marker-side read-only view).
export function colLetter(index) {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

// align: null is Excel's "General" alignment - numbers right, text left (see
// effectiveAlign). Cells saved before it existed carry an explicit 'left'
// and keep it.
function emptyCell() {
  return { value: '', bold: false, italic: false, underline: false, align: null, fontSize: null, fontColor: null, fillColor: null };
}

// Exported for reuse by SpreadsheetCellViewer.jsx (marker-side read-only view).
export function effectiveAlign(cell, cellEval) {
  if (cell && cell.align) return cell.align;
  return cellEval && !cellEval.isError && cellEval.numeric !== null && cellEval.numeric !== undefined ? 'right' : 'left';
}

// Everything about a cell except its contents - what the format painter,
// fill handle and in-grid paste carry across.
const FORMAT_FIELDS = ['bold', 'italic', 'underline', 'align', 'fontSize', 'fontColor', 'fillColor', 'borders', 'numFormat'];
function formatOf(cell) {
  const out = {};
  FORMAT_FIELDS.forEach(f => { out[f] = cell && cell[f] !== undefined ? cell[f] : (f === 'borders' || f === 'numFormat' ? undefined : emptyCell()[f]); });
  return out;
}
function withFormat(cell, format) {
  const next = { ...cell, ...format };
  if (!next.borders) delete next.borders;
  if (!next.numFormat) delete next.numFormat;
  return next;
}

const NUMBER_FORMATS = [
  { id: 'general', label: 'General', icon: Hash, format: null },
  { id: 'number0', label: 'Number  1,234', icon: Hash, format: { kind: 'number', decimals: 0, thousands: true } },
  { id: 'number2', label: 'Number  1,234.00', icon: Hash, format: { kind: 'number', decimals: 2, thousands: true } },
  { id: 'brackets0', label: 'Negatives in brackets  (1,234)', icon: Hash, format: { kind: 'number', decimals: 0, thousands: true, negBrackets: true } },
  { id: 'gbp', label: 'Currency  £1,234', icon: PoundSterling, format: { kind: 'currency', symbol: '£', decimals: 0, thousands: true } },
  { id: 'usd', label: 'Currency  $1,234', icon: DollarSign, format: { kind: 'currency', symbol: '$', decimals: 0, thousands: true } },
  { id: 'eur', label: 'Currency  €1,234', icon: Euro, format: { kind: 'currency', symbol: '€', decimals: 0, thousands: true } },
  { id: 'percent0', label: 'Percentage  12%', icon: Percent, format: { kind: 'percent', decimals: 0 } },
  { id: 'percent1', label: 'Percentage  12.3%', icon: Percent, format: { kind: 'percent', decimals: 1 } }
];

const CURRENCY_SYMBOLS = [
  { id: '£', label: '£  Pound' },
  { id: '$', label: '$  Dollar' },
  { id: '€', label: '€  Euro' },
  { id: '¥', label: '¥  Yen / Yuan' },
  { id: '₹', label: '₹  Rupee' }
];

// Cell borders - the lines accountancy workings use for subtotals and
// totals (ACCA's own exam spreadsheet offers them via Format Cells). Stored
// per cell as an optional `borders: { top, bottom, left, right }`, each
// 'thin' | 'thick' | 'double'; a cell with no borders has no `borders` key
// at all, so every grid saved before borders existed reads exactly as it
// did. Each shared edge is only ever stored on one of its two cells (see
// applyBorderPreset), so two different lines are never drawn on top of
// each other.
const BORDER_CSS = { thin: '1px solid #000', thick: '2px solid #000', double: '3px double #000' };

// Exported for reuse by SpreadsheetCellViewer.jsx (marker-side read-only view).
export function cellBorderCss(borders) {
  if (!borders || typeof borders !== 'object') return null;
  const css = {};
  ['top', 'bottom', 'left', 'right'].forEach(side => {
    const line = BORDER_CSS[borders[side]];
    if (line) css[`border${side[0].toUpperCase()}${side.slice(1)}`] = line;
  });
  return Object.keys(css).length ? css : null;
}

const BORDER_PRESETS = [
  { id: 'bottom', label: 'Bottom border', icon: PanelBottom },
  { id: 'top', label: 'Top border', icon: PanelTop },
  { id: 'left', label: 'Left border', icon: PanelLeft },
  { id: 'right', label: 'Right border', icon: PanelRight },
  { id: 'topBottom', label: 'Top and bottom border', icon: Rows2 },
  { id: 'thickBottom', label: 'Thick bottom border', icon: Minus },
  { id: 'doubleBottom', label: 'Double bottom border', icon: Equal },
  { id: 'topDoubleBottom', label: 'Top and double bottom border', icon: Equal },
  { id: 'outline', label: 'Outside borders', icon: Square },
  { id: 'all', label: 'All borders', icon: Grid2x2 },
  { id: 'none', label: 'No border', icon: Ban }
];

const CHAR_WIDTH_PX = 7.5; // rough per-character width at 13px Arial - same estimate used for text overflow sizing
const CELL_TEXT_PADDING = 10; // matches the input's own left+right padding (0 4px) plus a small margin

// Excel-style scientific notation: uppercase E, always-signed, 2+ digit
// exponent (e.g. "1E+05", not JS's own "1e+5").
function toExcelScientific(n, precision) {
  const exp = n.toExponential(precision);
  const m = /^(-?\d(?:\.\d+)?)e([+-])(\d+)$/i.exec(exp);
  if (!m) return String(n);
  const [, mantissa, sign, expDigits] = m;
  return `${mantissa}E${sign}${expDigits.padStart(2, '0')}`;
}

// A number that doesn't fit its cell never overflows into a neighbour (that
// would make it ambiguous which cell it belongs to) - instead it collapses
// to a run of # characters (numbers under 6 digits) or Excel-style
// scientific notation (6+ digits), shrinking precision until it fits,
// exactly like real Excel/Sheets.
function formatNumberForWidth(numeric, availableWidthPx) {
  const maxChars = Math.max(1, Math.floor(availableWidthPx / CHAR_WIDTH_PX));
  const digitCount = Math.abs(numeric) < 1 ? 1 : Math.floor(Math.log10(Math.abs(numeric))) + 1;

  if (digitCount < 6) return '#'.repeat(maxChars);

  for (let precision = 6; precision >= 0; precision--) {
    const candidate = toExcelScientific(numeric, precision);
    if (candidate.length <= maxChars) return candidate;
  }
  return '#'.repeat(maxChars);
}

// Accepts either the current {value, bold, italic, underline, align} shape,
// or a legacy plain-string cell from before formatting existed
function normalizeCell(cell) {
  if (cell && typeof cell === 'object') return { ...emptyCell(), ...cell };
  return { ...emptyCell(), value: cell || '' };
}

function isBlankCell(cell) {
  if (!cell) return true;
  return Object.entries(cell).every(([k, v]) => (k === 'value' ? !v : (v === null || v === undefined || v === false)));
}

// What actually gets saved: the grid trimmed to the last row/column the
// student has used (typed in or formatted) or resized, plus a margin of
// blank cells. The on-screen grid grows as they scroll, but saving every
// empty cell would bloat the exam session document (Firestore caps a
// document at 1 MiB). The margin matters: the marker's spreadsheet view
// shows exactly the saved grid, and markers put marks/comments on empty
// cells beside the work - before auto-growing sheets, saves always carried
// about 30 rows of blank space for that.
const SAVE_MIN_ROWS = 30;
const SAVE_MIN_COLS = 10;
const SAVE_MARGIN_ROWS = 10;
const SAVE_MARGIN_COLS = 4;
function serializeGrid(grid, colWidths) {
  let lastRow = 0;
  let lastCol = 0;
  grid.forEach((row, ri) => row.forEach((cell, ci) => {
    if (!isBlankCell(cell)) {
      if (ri > lastRow) lastRow = ri;
      if (ci > lastCol) lastCol = ci;
    }
  }));
  colWidths.forEach((w, ci) => { if (w !== DEFAULT_CELL_WIDTH && ci > lastCol) lastCol = ci; });
  lastRow = Math.min(grid.length - 1, Math.max(lastRow + SAVE_MARGIN_ROWS, SAVE_MIN_ROWS - 1));
  lastCol = Math.min((grid[0]?.length || 1) - 1, Math.max(lastCol + SAVE_MARGIN_COLS, SAVE_MIN_COLS - 1));
  return JSON.stringify({
    grid: grid.slice(0, lastRow + 1).map(row => row.slice(0, lastCol + 1).map(compactCell)),
    colWidths: colWidths.slice(0, lastCol + 1)
  });
}

// A cell as saved: only the fields that are actually set, so a blank cell
// is just {} (about 3 bytes instead of ~110). Every reader copes with the
// missing fields - parseGrid/normalizeCell fills them back in, and the PDF
// and Cloud Function readers only ever test fields for truthiness or use
// `cell.value || ''`. Matters because all of an exam's answers share one
// Firestore document, capped at 1 MiB.
function compactCell(cell) {
  const out = {};
  for (const [k, v] of Object.entries(cell || {})) {
    if (k === 'value' ? v : (v !== null && v !== undefined && v !== false)) out[k] = v;
  }
  return out;
}

// Pads a grid out with empty rows/columns to at least rows x cols.
function padGrid(grid, colWidths, rows, cols) {
  const curCols = grid[0]?.length || 0;
  let nextGrid = grid;
  let nextWidths = colWidths;
  if (curCols < cols) {
    nextGrid = nextGrid.map(row => [...row, ...Array.from({ length: cols - curCols }, emptyCell)]);
    nextWidths = [...colWidths, ...Array.from({ length: cols - curCols }, () => DEFAULT_CELL_WIDTH)];
  }
  const width = Math.max(curCols, cols);
  if (nextGrid.length < rows) {
    nextGrid = [...nextGrid, ...Array.from({ length: rows - nextGrid.length }, () => Array.from({ length: width }, emptyCell))];
  }
  return { grid: nextGrid, colWidths: nextWidths };
}

// The sheet as first drawn: the saved grid filled out to all 26 columns and
// at least OPEN_ROWS rows straight away, so it opens at its full size rather
// than at the (smaller) saved size and then jumping bigger a moment later
// once ensureFilled has measured the panel.
const OPEN_ROWS = 40;
function openingGrid(value) {
  const parsed = parseGrid(value);
  return padGrid(parsed.grid, parsed.colWidths, Math.min(MAX_ROWS, Math.max(parsed.grid.length, OPEN_ROWS)), MAX_COLS);
}

// Rows grow to fit a larger font, like Excel.
function rowHeightOf(row) {
  let maxFont = 13;
  for (const cell of row) if (cell.fontSize && cell.fontSize > maxFont) maxFont = cell.fontSize;
  return Math.max(ROW_HEIGHT, Math.ceil(maxFont * 1.45));
}

function defaultGrid() {
  return Array.from({ length: DEFAULT_ROWS }, () => Array.from({ length: DEFAULT_COLS }, emptyCell));
}

function defaultColWidths(cols) {
  return Array.from({ length: cols }, () => DEFAULT_CELL_WIDTH);
}

// Returns { grid, colWidths }. Handles three shapes: the current
// { grid, colWidths } object, a legacy bare array-of-rows, or nothing yet.
// Exported for reuse by SpreadsheetCellViewer.jsx (marker-side read-only view).
export function parseGrid(gridJson) {
  if (gridJson) {
    try {
      const parsed = JSON.parse(gridJson);
      if (parsed && Array.isArray(parsed.grid) && parsed.grid.length > 0) {
        const grid = parsed.grid.map(row => row.map(normalizeCell));
        const cols = grid[0].length;
        const colWidths = Array.isArray(parsed.colWidths) && parsed.colWidths.length === cols
          ? parsed.colWidths
          : defaultColWidths(cols);
        return { grid, colWidths };
      }
      if (Array.isArray(parsed) && parsed.length > 0) {
        const grid = parsed.map(row => row.map(normalizeCell));
        return { grid, colWidths: defaultColWidths(grid[0].length) };
      }
    } catch (e) { /* fall through to default */ }
  }
  return { grid: defaultGrid(), colWidths: defaultColWidths(DEFAULT_COLS) };
}

function normalizeSelection(sel) {
  if (!sel) return null;
  return {
    minRow: Math.min(sel.anchor.row, sel.focus.row),
    maxRow: Math.max(sel.anchor.row, sel.focus.row),
    minCol: Math.min(sel.anchor.col, sel.focus.col),
    maxCol: Math.max(sel.anchor.col, sel.focus.col)
  };
}

// Flat icon buttons, laid out like ACCA's own (iAssess) exam spreadsheet:
// no box around each one, just a light highlight on hover and a filled
// background while a toggle (bold, align, format painter) is on.
const FLAT_HOVER = '#e8eaed';
const flatButtonStyle = (active, disabled) => ({
  padding: '5px 6px',
  background: active ? '#d2e3fc' : 'transparent',
  color: active ? SELECTION_COLOR : '#3c4043',
  border: '1px solid transparent',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  opacity: disabled ? 0.35 : 1
});
const flatHover = (active, disabled) => ({
  onMouseEnter: (e) => { if (!disabled && !active) e.currentTarget.style.background = FLAT_HOVER; },
  onMouseLeave: (e) => { if (!active) e.currentTarget.style.background = 'transparent'; }
});

const ToolbarButton = ({ active, onClick, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()} // keep focus on the cell being formatted
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={flatButtonStyle(active, disabled)}
    {...flatHover(active, disabled)}
  >
    {children}
  </button>
);

const ToolbarDivider = () => (
  <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db', margin: '2px 6px' }} />
);

const STANDARD_COLORS = ['#000000', '#e74c3c', '#f39c12', '#f1c40f', '#2ecc71', '#3498db', '#9b59b6', '#7f8c8d'];

// Used for both font color and cell fill - an icon (Baseline/PaintBucket)
// with a thin bar underneath showing the current color, matching how Excel/
// Sheets distinguish these two buttons from plain formatting toggles. Offers
// 8 standard swatches plus a "more colors" option that opens the browser's
// own native color picker for anything else.
const ColorPickerButton = ({ icon: Icon, color, defaultColor, disabled, title, onChange, allowClear, clearTitle }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const nativeInputRef = useRef(null);
  const swatchColor = color || defaultColor;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        title={title}
        style={{ ...flatButtonStyle(false, disabled), flexDirection: 'column', gap: '2px' }}
        {...flatHover(false, disabled)}
      >
        <Icon size={14} color="#3c4043" />
        <div style={{ width: '16px', height: '3px', background: swatchColor, borderRadius: '1px', border: swatchColor === '#ffffff' ? '1px solid #d1d5db' : 'none' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 20,
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', padding: '8px',
          background: '#2c3e50', borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.3)', width: '108px'
        }}>
          {STANDARD_COLORS.map(c => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => { onChange(c); setOpen(false); }}
              style={{ width: '20px', height: '20px', borderRadius: '4px', background: c, border: '1px solid rgba(255,255,255,0.4)', cursor: 'pointer' }}
            />
          ))}
          {allowClear && (
            <button
              type="button"
              title={clearTitle || 'No fill'}
              onClick={() => { onChange(null); setOpen(false); }}
              style={{
                width: '20px', height: '20px', borderRadius: '4px', cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.4)', background: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <Ban size={12} color="#c33329" />
            </button>
          )}
          <button
            type="button"
            title="More colors..."
            onClick={() => nativeInputRef.current?.click()}
            style={{
              width: '20px', height: '20px', borderRadius: '4px', cursor: 'pointer',
              border: '1px solid rgba(255,255,255,0.4)',
              background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)'
            }}
          />
          <input
            ref={nativeInputRef}
            type="color"
            value={swatchColor}
            onChange={(e) => { onChange(e.target.value); setOpen(false); }}
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          />
        </div>
      )}
    </div>
  );
};

const MenuButton = ({ icon: ButtonIcon, title, items, disabled, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        title={title}
        style={{ ...flatButtonStyle(false, disabled), gap: '2px' }}
        {...flatHover(false, disabled)}
      >
        <ButtonIcon size={15} />
        <ChevronDown size={11} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 20,
          background: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: '210px'
        }}>
          {items.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                padding: '6px 12px', background: 'white', border: 'none', textAlign: 'left',
                fontSize: '12px', color: '#3c4043', cursor: 'pointer'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f3f4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              {Icon ? <Icon size={14} /> : null}
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// One spreadsheet row. Memoised so typing in a cell (or moving the
// selection) only redraws the rows it actually touches - redrawing every
// cell on every keystroke made a 200-row sheet take seconds per key. The
// parent passes this row's slice of the selection/fill/point state as plain
// numbers/booleans, and its event handlers through a ref (`api`) that never
// changes identity, so an untouched row compares equal and is skipped.
// How far text (never numbers - see the numeric-cell check at the render
// site) is allowed to overflow past its own cell: walks consecutive empty
// cells in the given direction, summing their widths, and stops at the
// first non-empty cell or the grid edge - matches Excel's own "overflow
// through blank cells, clip at the first cell that has something in it"
// behavior, including across more than one blank cell.
function overflowWidth(row, ci, align, widths) {
  const step = align === 'right' ? -1 : 1;
  let available = widths[ci];
  let idx = ci + step;
  while (idx >= 0 && idx < row.length) {
    const neighbour = row[idx];
    const neighbourEmpty = !neighbour || !neighbour.value || !String(neighbour.value).trim();
    if (!neighbourEmpty) break;
    available += widths[idx];
    idx += step;
  }
  return available;
}

function sameEvalRow(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (x === y) continue;
    if (!x || !y || x.display !== y.display || x.numeric !== y.numeric || x.isError !== y.isError) return false;
  }
  return true;
}

function gridRowPropsEqual(prev, next) {
  for (const k of Object.keys(next)) {
    if (k === 'evalRow') { if (!sameEvalRow(prev.evalRow, next.evalRow)) return false; }
    else if (prev[k] !== next[k]) return false;
  }
  return true;
}

const GridRow = React.memo(function GridRow({
  ri, row, evalRow, widths, disabled, api,
  inBoxRow, anchorCol, pointMinCol, pointMaxCol, pointTop, pointBottom
}) {
  const rowH = rowHeightOf(row);
  return (
    <tr key={ri}>
      <th
        onClick={() => api.current.selectRow(ri)}
        style={{
          position: 'sticky', left: 0, zIndex: 3,
          width: `${ROW_HEADER_WIDTH}px`, height: `${rowH}px`, padding: 0,
          background: inBoxRow ? '#d2e3fc' : '#f1f3f4',
          color: inBoxRow ? SELECTION_COLOR : '#5f6368',
          border: '1px solid #dadce0',
          fontSize: '12px', fontWeight: '500', cursor: 'pointer', userSelect: 'none'
        }}
      >
        {ri + 1}
      </th>
      {row.map((cell, ci) => {
        const w = widths[ci];
        const isAnchor = ci === anchorCol;

        // The active cell shows its raw text for editing (a
        // formula included, exactly like Excel), and - like Excel
        // - lets it overflow visibly while typing regardless of
        // whether it's text or a number, since nothing is
        // "committed" yet. Every other cell shows the computed
        // result: text overflows into empty neighbouring cells
        // (clipping the moment one has something in it), but a
        // number never does - a number that doesn't fit collapses
        // to #### or scientific notation instead, exactly like
        // Excel, since a partially-overflowed number would be
        // ambiguous about which cell it belongs to.
        const cellEval = evalRow?.[ci];
        const align = effectiveAlign(cell, cellEval);
        const rawDisplay = isAnchor ? cell.value : (cellEval ? cellEval.display : cell.value);
        const showAsError = !isAnchor && cellEval?.isError;
        const isCommittedNumber = !isAnchor && cellEval && cellEval.numeric !== null && !cellEval.isError;
        const borderCss = cellBorderCss(cell.borders);

        const neededWidth = Math.max(w, String(rawDisplay || '').length * CHAR_WIDTH_PX + CELL_TEXT_PADDING);
        let displayValue = rawDisplay;
        let displayWidth = w;
        if (isCommittedNumber) {
          // A number with a number format that doesn't fit shows
          // #### rather than switching to scientific, like Excel.
          displayValue = neededWidth > w
            ? (cell.numFormat ? '#'.repeat(Math.max(1, Math.floor((w - CELL_TEXT_PADDING) / CHAR_WIDTH_PX))) : formatNumberForWidth(cellEval.numeric, w - CELL_TEXT_PADDING))
            : rawDisplay;
        } else {
          displayWidth = Math.min(neededWidth, overflowWidth(row, ci, align, widths));
        }

        return (
          <td
            key={ci}
            onMouseDown={(e) => api.current.handleCellMouseDown(e, ri, ci)}
            onMouseEnter={() => api.current.handleCellMouseEnter(ri, ci)}
            onContextMenu={(e) => api.current.handleCellContextMenu(e)}
            onDragStart={(e) => e.preventDefault()}
            style={{
              position: 'relative', width: `${w}px`, height: `${rowH}px`, padding: 0,
              // The selection outline/tint and the fill handle are NOT
              // drawn here - see the selection overlay in the parent.
              // Changing cell borders made Chrome re-lay-out and repaint
              // the whole table on every click.
              background: cell.fillColor || 'white',
              border: '1px solid #e2e8f0'
            }}
          >
            <input
              type="text"
              ref={(el) => { const refs = api.current.cellRefs.current; if (el) refs[`${ri}-${ci}`] = el; else delete refs[`${ri}-${ci}`]; }}
              value={displayValue}
              disabled={disabled}
              title={showAsError ? 'This formula could not be calculated' : undefined}
              onChange={(e) => api.current.updateCell(ri, ci, { value: e.target.value })}
              onKeyDown={(e) => api.current.handleCellKeyDown(e, ri, ci)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                // Always exactly the cell's own width, even while
                // overflowing visually (see the decorative overlay
                // below) - a real, clickable input stretched over
                // a neighbouring cell would sit on top of that
                // cell's own input and swallow clicks meant for
                // it, which is exactly the bug this fixes.
                width: `${w}px`,
                boxSizing: 'border-box',
                padding: '0 4px',
                border: 'none',
                outline: 'none',
                fontSize: `${cell.fontSize || 13}px`,
                fontFamily: 'inherit',
                background: 'transparent',
                color: showAsError ? '#c33329' : (cell.fontColor || 'inherit'),
                fontWeight: cell.bold ? '700' : '400',
                fontStyle: cell.italic ? 'italic' : 'normal',
                textDecoration: cell.underline ? 'underline' : 'none',
                textAlign: align
              }}
            />
            {/* Purely visual overflow into empty neighbouring
                cells - pointer-events:none so a click anywhere in
                that space passes straight through to whatever
                cell actually occupies it, instead of this one. */}
            {!isAnchor && displayWidth > w && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: `${displayWidth}px`,
                  boxSizing: 'border-box',
                  ...(align === 'right' ? { right: 0 } : { left: 0 }),
                  pointerEvents: 'none',
                  zIndex: 1,
                  padding: '0 4px',
                  lineHeight: `${rowH - 1}px`,
                  fontSize: `${cell.fontSize || 13}px`,
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  background: cell.fillColor || 'white',
                  color: cell.fontColor || 'inherit',
                  fontWeight: cell.bold ? '700' : '400',
                  fontStyle: cell.italic ? 'italic' : 'normal',
                  textDecoration: cell.underline ? 'underline' : 'none',
                  textAlign: align
                }}
              >
                {displayValue}
              </div>
            )}
            {/* The cell's own borders, drawn on top of its
                gridlines (and above any neighbouring text
                overflowing across it, like Excel) - nudged out 1px
                so they sit exactly on the gridline and join up
                with the next cell's border. */}
            {borderCss && (
              <div
                style={{
                  position: 'absolute', top: '-1px', left: '-1px', right: '-1px', bottom: '-1px',
                  pointerEvents: 'none', zIndex: 2,
                  ...borderCss
                }}
              />
            )}
            {pointMinCol >= 0 && ci >= pointMinCol && ci <= pointMaxCol && (
              <div
                style={{
                  position: 'absolute', top: '-1px', left: '-1px', right: '-1px', bottom: '-1px',
                  pointerEvents: 'none', zIndex: 3, background: 'rgba(26,115,232,0.08)',
                  borderTop: pointTop ? `2px dashed ${SELECTION_COLOR}` : 'none',
                  borderBottom: pointBottom ? `2px dashed ${SELECTION_COLOR}` : 'none',
                  borderLeft: ci === pointMinCol ? `2px dashed ${SELECTION_COLOR}` : 'none',
                  borderRight: ci === pointMaxCol ? `2px dashed ${SELECTION_COLOR}` : 'none'
                }}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
}, gridRowPropsEqual);

const ExamSpreadsheetGrid = ({ value, onChange, disabled }) => {
  const [grid, setGrid] = useState(() => openingGrid(value).grid);
  const [colWidths, setColWidths] = useState(() => openingGrid(value).colWidths);
  // A real spreadsheet always has a cell selected (A1 on open) - matches that
  // rather than requiring a click before the reference box/formula bar mean
  // anything. `anchor` is the cell the formula bar edits; `focus` is the far
  // corner of a dragged range - equal to anchor for an ordinary single click.
  const [selection, setSelection] = useState({ anchor: { row: 0, col: 0 }, focus: { row: 0, col: 0 } });
  const [previewWidth, setPreviewWidth] = useState(null); // { colIndex, width } while dragging a column border
  const [zoom, setZoom] = useState(1); // visual magnification only - column widths/font sizes stay in real px underneath
  // Undo/redo history is in-memory only (not persisted) - same as a real
  // spreadsheet losing its undo stack when you close and reopen the file.
  const [, forceHistoryRerender] = useState(0);
  const historyRef = useRef({ past: [], future: [] });

  const cellRefs = useRef({}); // `${row}-${col}` -> input element, for arrow-key/Tab navigation
  const formulaBarRef = useRef(null); // the formula bar's own input element
  const isSelectingRef = useRef(false); // mouse button down and dragging a cell range
  const [contextMenu, setContextMenu] = useState(null); // right-click menu position
  const keepBrowserMenuRef = useRef(false); // right-clicked on words selected in one cell
  const resizeRef = useRef(null); // { colIndex, startX, startWidth }
  const previewWidthRef = useRef(null); // mirrors previewWidth for the mouseup handler below
  // Set while dragging out a reference for a formula in progress (see
  // insertReference below) - { row, col, inputEl, insertStart, insertEnd },
  // where insertStart/insertEnd mark the span of the just-inserted reference
  // text so dragging further replaces it rather than appending another one.
  const pointModeDragRef = useRef(null);
  // What was last copied/cut from THIS grid - { text, cells, origin, isCut }.
  // Pasting that same text back pastes the cells themselves (formatting
  // included, formula references adjusted) instead of plain text.
  const clipboardRef = useRef(null);
  // The reference point mode last inserted - { key, start, end, value,
  // pointer } - so clicking another cell (or pressing an arrow key) straight
  // away replaces it instead of committing, as in Excel. pointer is the
  // { anchor, focus } range it refers to, outlined in the grid and moved by
  // the arrow keys.
  const lastPointRef = useRef(null);
  // Format painter: the copied formats (a 2D block, tiled over the target
  // like Excel), armed until the next selection is made.
  const [paintFormat, setPaintFormat] = useState(null);
  // Fill handle drag: the source range, and the range it will fill to.
  const fillDragRef = useRef(null);
  const [fillTarget, setFillTarget] = useState(null);
  // The range being dragged out right now, kept in step with the mouse
  // (rather than read back from state) so the mouseup that finishes a fill
  // or format-painter drag always uses exactly what was under the pointer.
  const dragBoxRef = useRef(null);
  const fillTargetRef = useRef(null);
  const updateFillTarget = (target) => { fillTargetRef.current = target; setFillTarget(target); };

  // Snapshots the pre-edit grid/colWidths onto the undo stack and clears any
  // redo history, since a fresh edit invalidates it. Call this before every
  // committed change (not on every keystroke re-render - callers control
  // when a "change" is granular enough to warrant its own undo step).
  const pushHistory = () => {
    historyRef.current.past.push({ grid, colWidths });
    if (historyRef.current.past.length > 100) historyRef.current.past.shift();
    historyRef.current.future = [];
    forceHistoryRerender(v => v + 1);
  };

  // Re-seed if the value changes externally (e.g. on initial load from Firestore)
  useEffect(() => {
    const parsed = openingGrid(value);
    setGrid(parsed.grid);
    setColWidths(parsed.colWidths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback((nextGrid, nextColWidths) => {
    setGrid(nextGrid);
    setColWidths(nextColWidths);
    onChange(serializeGrid(nextGrid, nextColWidths));
  }, [onChange]);

  // Coalesces a burst of keystrokes into one undo step: only the first
  // change to a given cell since it was last focused pushes a history
  // snapshot (reset in moveTo/handleCellMouseDown whenever the active cell
  // changes), rather than every single keystroke getting its own undo.
  const editingCellRef = useRef(null);

  const updateCell = (row, col, changes) => {
    const key = `${row}-${col}`;
    if (editingCellRef.current !== key) {
      pushHistory();
      editingCellRef.current = key;
    }
    const next = grid.map((r, ri) => ri === row
      ? r.map((c, ci) => ci === col ? { ...c, ...changes } : c)
      : r);
    emit(next, colWidths);
  };

  const applyToSelection = (changesFn) => {
    const box = normalizeSelection(selection);
    if (!box) return;
    pushHistory();
    const next = grid.map((row, ri) => {
      if (ri < box.minRow || ri > box.maxRow) return row;
      return row.map((cell, ci) => (ci < box.minCol || ci > box.maxCol) ? cell : { ...cell, ...changesFn(cell) });
    });
    emit(next, colWidths);
  };

  // Excel's Borders menu: edge presets (bottom/top/...) apply to the
  // selected range's outer edge only, 'all' to every cell edge inside it,
  // 'none' clears every edge of every selected cell. Setting or clearing an
  // edge also clears the neighbouring cell's copy of that same shared edge,
  // so each line on screen is stored exactly once.
  const applyBorderPreset = (preset) => {
    const box = normalizeSelection(selection);
    if (!box) return;
    pushHistory();
    const next = grid.map(row => [...row]);
    const setSide = (r, c, side, style) => {
      const cell = next[r]?.[c];
      if (!cell) return;
      const borders = { ...(cell.borders || {}) };
      if (style) borders[side] = style; else delete borders[side];
      const nextCell = { ...cell, borders };
      if (!Object.keys(borders).length) delete nextCell.borders;
      next[r][c] = nextCell;
    };
    const OPPOSITE = { top: ['bottom', -1, 0], bottom: ['top', 1, 0], left: ['right', 0, -1], right: ['left', 0, 1] };
    const setEdge = (r, c, side, style) => {
      const [opp, dr, dc] = OPPOSITE[side];
      setSide(r + dr, c + dc, opp, null);
      setSide(r, c, side, style);
    };
    const eachCol = (fn) => { for (let c = box.minCol; c <= box.maxCol; c++) fn(c); };
    const eachRow = (fn) => { for (let r = box.minRow; r <= box.maxRow; r++) fn(r); };
    const eachCell = (fn) => eachRow(r => eachCol(c => fn(r, c)));
    const top = (style) => eachCol(c => setEdge(box.minRow, c, 'top', style));
    const bottom = (style) => eachCol(c => setEdge(box.maxRow, c, 'bottom', style));
    const left = (style) => eachRow(r => setEdge(r, box.minCol, 'left', style));
    const right = (style) => eachRow(r => setEdge(r, box.maxCol, 'right', style));

    switch (preset) {
      case 'bottom': bottom('thin'); break;
      case 'top': top('thin'); break;
      case 'left': left('thin'); break;
      case 'right': right('thin'); break;
      case 'topBottom': top('thin'); bottom('thin'); break;
      case 'thickBottom': bottom('thick'); break;
      case 'doubleBottom': bottom('double'); break;
      case 'topDoubleBottom': top('thin'); bottom('double'); break;
      case 'outline': top('thin'); bottom('thin'); left('thin'); right('thin'); break;
      case 'all': eachCell((r, c) => ['top', 'bottom', 'left', 'right'].forEach(side => setEdge(r, c, side, 'thin'))); break;
      case 'none': eachCell((r, c) => ['top', 'bottom', 'left', 'right'].forEach(side => setEdge(r, c, side, null))); break;
      default: return;
    }
    emit(next, colWidths);
  };

  // Like applyToSelection, but the callback returns the whole new cell (so
  // it can remove fields) and gets the cell's position.
  const mapSelection = (fn) => {
    const box = normalizeSelection(selection);
    if (!box) return;
    pushHistory();
    const next = grid.map((row, ri) => {
      if (ri < box.minRow || ri > box.maxRow) return row;
      return row.map((cell, ci) => (ci < box.minCol || ci > box.maxCol) ? cell : fn(cell, ri, ci));
    });
    emit(next, colWidths);
  };

  const applyNumberFormat = (id) => {
    const preset = NUMBER_FORMATS.find(f => f.id === id);
    if (!preset) return;
    mapSelection(cell => withFormat(cell, { numFormat: preset.format ? { ...preset.format } : undefined }));
  };

  // Increase/Decrease Decimal - on a cell with no number format yet, starts
  // from however many decimal places it's currently showing, like Excel.
  const changeDecimals = (delta) => {
    mapSelection((cell, ri, ci) => {
      const current = cell.numFormat || { kind: 'number', decimals: visibleDecimals(evaluated[ri]?.[ci]?.display), thousands: false };
      const decimals = Math.min(10, Math.max(0, (current.decimals || 0) + delta));
      return { ...cell, numFormat: { ...current, decimals } };
    });
  };

  // Inserts a currency symbol where the text cursor is in the selected
  // cell (or formula bar), or at the end of the cell if neither is being
  // typed into.
  const insertSymbol = (symbol) => {
    const { row, col } = selection.anchor;
    const currentValue = grid[row]?.[col]?.value || '';
    const input = getFormulaEntryInput();
    const start = input ? input.selectionStart : currentValue.length;
    const end = input ? input.selectionEnd : currentValue.length;
    updateCell(row, col, { value: currentValue.slice(0, start) + symbol + currentValue.slice(end) });
    const target = input || cellRefs.current[`${row}-${col}`];
    requestAnimationFrame(() => {
      if (!target) return;
      target.focus();
      target.setSelectionRange(start + symbol.length, start + symbol.length);
    });
  };

  const togglePainter = () => {
    if (paintFormat) { setPaintFormat(null); return; }
    const box = normalizeSelection(selection);
    if (!box) return;
    const block = [];
    for (let r = box.minRow; r <= box.maxRow; r++) {
      const row = [];
      for (let c = box.minCol; c <= box.maxCol; c++) row.push(formatOf(grid[r][c]));
      block.push(row);
    }
    setPaintFormat(block);
  };

  const applyPaint = (dragSel) => {
    const block = paintFormat;
    setPaintFormat(null);
    const box = normalizeSelection(dragSel || selection);
    if (!block || !box) return;
    const h = block.length;
    const w = block[0].length;
    pushHistory();
    const next = grid.map((row, ri) => {
      if (ri < box.minRow || ri > box.maxRow) return row;
      return row.map((cell, ci) => (ci < box.minCol || ci > box.maxCol) ? cell : withFormat(cell, block[(ri - box.minRow) % h][(ci - box.minCol) % w]));
    });
    emit(next, colWidths);
  };

  // Fill handle: repeats the source range across the dragged-to area - plain
  // values copied exactly (1, 2 fills as 1, 2, 1, 2 - the same as ACCA's own
  // spreadsheet, which doesn't extend series), formulas with their relative
  // references moved along, formatting carried with them.
  const applyFill = (src, target) => {
    const h = src.maxRow - src.minRow + 1;
    const w = src.maxCol - src.minCol + 1;
    const mod = (n, m) => ((n % m) + m) % m;
    pushHistory();
    const next = grid.map(row => [...row]);
    for (let r = target.minRow; r <= target.maxRow; r++) {
      for (let c = target.minCol; c <= target.maxCol; c++) {
        if (r >= src.minRow && r <= src.maxRow && c >= src.minCol && c <= src.maxCol) continue;
        const sr = src.minRow + mod(r - src.minRow, h);
        const sc = src.minCol + mod(c - src.minCol, w);
        const source = grid[sr][sc];
        next[r][c] = withFormat({ ...next[r][c], value: shiftFormula(source.value || '', r - sr, c - sc) }, formatOf(source));
      }
    }
    emit(next, colWidths);
    setSelection({ anchor: { row: target.minRow, col: target.minCol }, focus: { row: target.maxRow, col: target.maxCol } });
  };

  const focusCell = (row, col) => {
    cellRefs.current[`${row}-${col}`]?.focus();
  };

  // Moves the active cell (plain arrow/Tab/Enter) or extends the current
  // range without moving DOM focus (Shift+arrow) - matches how Excel/Sheets
  // handle range selection with the keyboard.
  const moveTo = (row, col, extend) => {
    const rows = grid.length;
    const cols = grid[0]?.length || 0;
    const nr = Math.min(Math.max(row, 0), rows - 1);
    const nc = Math.min(Math.max(col, 0), cols - 1);
    if (extend) {
      setSelection(sel => ({ anchor: sel ? sel.anchor : { row: nr, col: nc }, focus: { row: nr, col: nc } }));
    } else {
      editingCellRef.current = null;
      lastPointRef.current = null;
      setSelection({ anchor: { row: nr, col: nc }, focus: { row: nr, col: nc } });
      focusCell(nr, nc);
    }
  };

  // Shift+arrow: grows/shrinks the range from its far corner (the focus),
  // not from the active cell - so Shift+Down twice selects three rows, as in
  // Excel. (It used to restart from the active cell each time, so the range
  // could never get more than one cell past it.)
  const extendBy = (dr, dc) => {
    const rows = grid.length;
    const cols = grid[0]?.length || 0;
    const nr = Math.min(Math.max(selection.focus.row + dr, 0), rows - 1);
    const nc = Math.min(Math.max(selection.focus.col + dc, 0), cols - 1);
    setSelection(sel => ({ anchor: sel.anchor, focus: { row: nr, col: nc } }));
    cellRefs.current[`${nr}-${nc}`]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const clearSelectionValues = () => applyToSelection(() => ({ value: '' }));

  const handleUndo = () => {
    if (!historyRef.current.past.length) return;
    editingCellRef.current = null;
    const popped = historyRef.current.past.pop();
    historyRef.current.future.push({ grid, colWidths });
    // Keep the sheet at least as big as it is now (it may have grown since
    // the snapshot), so the selection never points past the last row.
    const prevState = padGrid(popped.grid, popped.colWidths, grid.length, grid[0]?.length || 0);
    setGrid(prevState.grid);
    setColWidths(prevState.colWidths);
    onChange(serializeGrid(prevState.grid, prevState.colWidths));
    forceHistoryRerender(v => v + 1);
  };

  const handleRedo = () => {
    if (!historyRef.current.future.length) return;
    editingCellRef.current = null;
    const popped = historyRef.current.future.pop();
    historyRef.current.past.push({ grid, colWidths });
    const nextState = padGrid(popped.grid, popped.colWidths, grid.length, grid[0]?.length || 0);
    setGrid(nextState.grid);
    setColWidths(nextState.colWidths);
    onChange(serializeGrid(nextState.grid, nextState.colWidths));
    forceHistoryRerender(v => v + 1);
  };

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;

  // Up/Down/Tab/Enter always move a cell (a single-line input has no other
  // use for them). Left/Right only move a cell once the text cursor is
  // already at that edge of the cell's content, so normal in-cell cursor
  // movement while typing/editing still works as expected. Shift+arrow
  // extends the selected range instead of moving.
  // Keyboard point mode, as in Excel: while typing a formula into a cell,
  // an arrow key straight after =, an operator, "(", "," or ":" inserts a
  // reference to the neighbouring cell instead of leaving the cell. More
  // arrows move that reference; Shift+arrow stretches it into a range.
  // Returns true if it handled the key.
  const handleKeyboardPoint = (e, row, col) => {
    const moves = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    if (!moves[e.key] || e.ctrlKey || e.metaKey || e.altKey || paintFormat) return false;
    const key = `${row}-${col}`;
    const value = grid[row]?.[col]?.value || '';
    const input = e.target;
    if (!value.startsWith('=') || editingCellRef.current !== key || input.selectionStart !== input.selectionEnd) return false;
    const caret = input.selectionStart;
    const last = lastPointRef.current;
    let replaceRange, pointer;
    if (last && last.pointer && last.key === key && last.value === value && caret === last.end) {
      replaceRange = { start: last.start, end: last.end };
      pointer = last.pointer;
    } else if (/[=+\-*/^(,:]\s*$/.test(value.slice(0, caret))) {
      replaceRange = { start: caret, end: caret };
      pointer = null;
    } else {
      return false;
    }
    e.preventDefault();
    const [dr, dc] = moves[e.key];
    const from = pointer ? pointer.focus : { row, col };
    const to = {
      row: Math.min(Math.max(from.row + dr, 0), grid.length - 1),
      col: Math.min(Math.max(from.col + dc, 0), (grid[0]?.length || 1) - 1)
    };
    const nextPointer = e.shiftKey && pointer ? { anchor: pointer.anchor, focus: to } : { anchor: to, focus: to };
    insertReference(input, pointerRefText(nextPointer), replaceRange, nextPointer);
    cellRefs.current[`${to.row}-${to.col}`]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return true;
  };

  const handleCellKeyDown = (e, row, col) => {
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;
    if (handleKeyboardPoint(e, row, col)) return;
    if (isCtrlOrCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      handleUndo();
    } else if (isCtrlOrCmd && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault();
      handleRedo();
    } else if (isCtrlOrCmd && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault();
      toggleActive({ b: 'bold', i: 'italic', u: 'underline' }[e.key.toLowerCase()]);
    } else if (e.key === 'Escape' && paintFormat) {
      e.preventDefault();
      setPaintFormat(null);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (e.shiftKey) extendBy(-1, 0); else moveTo(row - 1, col, false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (e.shiftKey) extendBy(1, 0); else moveTo(row + 1, col, false);
    } else if (e.key === 'ArrowLeft') {
      const input = e.target;
      if (e.shiftKey || (input.selectionStart === 0 && input.selectionEnd === 0)) {
        e.preventDefault();
        if (e.shiftKey) extendBy(0, -1); else moveTo(row, col - 1, false);
      }
    } else if (e.key === 'ArrowRight') {
      const input = e.target;
      const len = input.value.length;
      if (e.shiftKey || (input.selectionStart === len && input.selectionEnd === len)) {
        e.preventDefault();
        if (e.shiftKey) extendBy(0, 1); else moveTo(row, col + 1, false);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      moveTo(row + (e.shiftKey ? -1 : 1), col, false);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      moveTo(row, col + (e.shiftKey ? -1 : 1), false);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      const box = normalizeSelection(selection);
      if (box && (box.minRow !== box.maxRow || box.minCol !== box.maxCol)) {
        e.preventDefault();
        clearSelectionValues();
      }
      // single-cell selection: let the input handle Backspace/Delete itself
    }
  };

  // The formula bar only needs Enter/Tab/Escape handled specially - anything
  // else (including Backspace/Delete) should behave like an ordinary text
  // input, unlike handleCellKeyDown above which also clears a multi-cell
  // range on Backspace/Delete (not appropriate here: the formula bar always
  // edits a single cell's text, even when a multi-cell range happens to be
  // selected behind it).
  const handleFormulaBarKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      moveTo(selection.anchor.row + (e.shiftKey ? -1 : 1), selection.anchor.col, false);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      moveTo(selection.anchor.row, selection.anchor.col + (e.shiftKey ? -1 : 1), false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      focusCell(selection.anchor.row, selection.anchor.col);
    }
  };

  // Which real DOM input is currently being typed into for the SELECTED
  // cell - the formula bar, or that cell's own input - or null if neither
  // has focus right now. Used to drive "point mode" below: clicking another
  // cell only inserts a reference if the thing you're actually typing into
  // is one of these two (both edit the same underlying cell value).
  const getFormulaEntryInput = () => {
    const activeEl = document.activeElement;
    if (activeEl === formulaBarRef.current) return activeEl;
    if (activeEl === cellRefs.current[`${selection.anchor.row}-${selection.anchor.col}`]) return activeEl;
    return null;
  };

  const pointerRefText = ({ anchor, focus }) => (anchor.row === focus.row && anchor.col === focus.col)
    ? colLetter(focus.col) + (focus.row + 1)
    : `${colLetter(Math.min(anchor.col, focus.col))}${Math.min(anchor.row, focus.row) + 1}:${colLetter(Math.max(anchor.col, focus.col))}${Math.max(anchor.row, focus.row) + 1}`;

  // Inserts refText into the selected cell's formula at the given (or
  // current) cursor position, then restores focus and cursor position to
  // just after it - matches Excel/Sheets' "point mode": clicking a cell
  // while composing a formula inserts its reference without navigating
  // there, so =, click, +, click, Enter builds =A1+A2 without ever leaving
  // the formula bar.
  const insertReference = (inputEl, refText, replaceRange, pointer = null) => {
    const { row, col } = selection.anchor;
    const currentValue = grid[row]?.[col]?.value || '';
    const start = replaceRange ? replaceRange.start : inputEl.selectionStart;
    const end = replaceRange ? replaceRange.end : inputEl.selectionEnd;
    const nextValue = currentValue.slice(0, start) + refText + currentValue.slice(end);
    updateCell(row, col, { value: nextValue });
    const caret = start + refText.length;
    lastPointRef.current = { key: `${row}-${col}`, start, end: caret, value: nextValue, pointer };
    // Next tick, once the input has re-rendered with nextValue - setting the
    // selection range this tick would still see the old, shorter value.
    requestAnimationFrame(() => {
      inputEl.focus({ preventScroll: true });
      inputEl.setSelectionRange(caret, caret);
    });
    return { start, end: caret };
  };

  const handleCellMouseDown = (e, row, col) => {
    const clickedKey = `${row}-${col}`;
    const clickedInput = cellRefs.current[clickedKey];

    // Right-click (or Ctrl+click on a Mac) opens the menu, like Excel: inside
    // the selection it keeps the selection, outside it moves to that cell.
    if (e.button === 2 || (e.button === 0 && e.ctrlKey && IS_MAC)) {
      const current = normalizeSelection(selection);
      const inside = current && row >= current.minRow && row <= current.maxRow && col >= current.minCol && col <= current.maxCol;
      const isRange = current && (current.minRow !== current.maxRow || current.minCol !== current.maxCol);
      // Words picked out of a single cell keep the browser's own menu
      keepBrowserMenuRef.current = !isRange && document.activeElement === clickedInput && clickedInput?.selectionStart !== clickedInput?.selectionEnd;
      if (inside) {
        // Stop focus moving to the clicked cell and away from the active one
        if (isRange) e.preventDefault();
        return;
      }
      editingCellRef.current = null;
      lastPointRef.current = null;
      setSelection({ anchor: { row, col }, focus: { row, col } });
      return;
    }

    // Clicking inside the very cell you're typing into just repositions the
    // text cursor (e.g. to fix a typo mid-formula), not a point-mode reference
    // click - let the browser place the caret natively instead of falling into
    // the branch below, which would otherwise insert a self-reference at the
    // OLD cursor position and corrupt the formula. If the cell isn't being
    // typed into, carry on: a drag can start from the active cell like any other.
    if (document.activeElement === clickedInput && editingCellRef.current === clickedKey) return;

    // Point mode (clicking a cell inserts its reference) works like Excel's:
    // only while a formula is actually being typed (in the formula bar, or
    // in a cell typed into since it was selected), and only where the
    // formula is waiting for a reference - straight after =, an operator,
    // "(", "," or ":" - or to swap the reference just clicked in. Anywhere
    // else (e.g. after typing =B1+B2), clicking a cell finishes the entry and
    // moves there, instead of tacking a reference onto the end. Never while
    // the format painter is armed.
    const anchorKey = `${selection.anchor.row}-${selection.anchor.col}`;
    const anchorValue = grid[selection.anchor.row]?.[selection.anchor.col]?.value || '';
    const entryInput = anchorValue.startsWith('=') && !paintFormat ? getFormulaEntryInput() : null;
    const formulaInput = entryInput && (entryInput === formulaBarRef.current || editingCellRef.current === anchorKey)
      ? entryInput
      : null;
    let pointRange = null;
    if (formulaInput) {
      const caretStart = formulaInput.selectionStart;
      const caretEnd = formulaInput.selectionEnd;
      const last = lastPointRef.current;
      if (last && last.key === anchorKey && last.value === anchorValue && caretStart === last.end && caretEnd === last.end) {
        pointRange = { start: last.start, end: last.end };
      } else if (/[=+\-*/^(,:]\s*$/.test(anchorValue.slice(0, caretStart))) {
        pointRange = { start: caretStart, end: caretEnd };
      }
    }
    if (pointRange) {
      e.preventDefault(); // don't let focus/the text cursor move to this cell
      const range = insertReference(formulaInput, colLetter(col) + (row + 1), pointRange, { anchor: { row, col }, focus: { row, col } });
      pointModeDragRef.current = { row, col, inputEl: formulaInput, insertStart: range.start, insertEnd: range.end };
      return;
    }
    editingCellRef.current = null;
    lastPointRef.current = null;
    isSelectingRef.current = true;
    dragBoxRef.current = { anchor: { row, col }, focus: { row, col } };
    setSelection({ anchor: { row, col }, focus: { row, col } });
  };

  const handleCellMouseEnter = (row, col) => {
    if (fillDragRef.current) {
      const b = fillDragRef.current.box;
      const below = row - b.maxRow, above = b.minRow - row, right = col - b.maxCol, left = b.minCol - col;
      const vertical = Math.max(below, above), horizontal = Math.max(right, left);
      if (vertical <= 0 && horizontal <= 0) updateFillTarget(null);
      else if (vertical >= horizontal) updateFillTarget({ ...b, minRow: Math.min(b.minRow, row), maxRow: Math.max(b.maxRow, row) });
      else updateFillTarget({ ...b, minCol: Math.min(b.minCol, col), maxCol: Math.max(b.maxCol, col) });
      return;
    }
    if (pointModeDragRef.current) {
      const { row: startRow, col: startCol, inputEl, insertStart, insertEnd } = pointModeDragRef.current;
      const pointer = { anchor: { row: startRow, col: startCol }, focus: { row, col } };
      const range = insertReference(inputEl, pointerRefText(pointer), { start: insertStart, end: insertEnd }, pointer);
      pointModeDragRef.current.insertStart = range.start;
      pointModeDragRef.current.insertEnd = range.end;
      return;
    }
    if (!isSelectingRef.current) return;
    if (dragBoxRef.current) dragBoxRef.current = { anchor: dragBoxRef.current.anchor, focus: { row, col } };
    setSelection(sel => sel ? { anchor: sel.anchor, focus: { row, col } } : sel);
  };

  // The global mouseup/mousemove listeners below stay subscribed for the
  // component's whole lifetime, so they can't close over the latest
  // `grid`/`colWidths`/`emit` from render - those go through a ref instead.
  const latestRef = useRef({ grid, colWidths, emit, pushHistory, zoom });
  useEffect(() => { latestRef.current = { grid, colWidths, emit, pushHistory, zoom, applyFill, paintFormat, applyPaint }; });

  // The sheet is capped at MAX_ROWS x MAX_COLS. Columns are filled out to
  // the cap straight away; rows are only created as they're needed: whenever
  // the visible area gets within GROW_MARGIN_PX of the last row (on open, on
  // resize, on zoom, on scroll, or when the keyboard moves the active cell
  // there), more are appended, up to the cap. Not an undoable action and not saved
  // on its own (serializeGrid drops empty cells anyway) - it's purely so the
  // sheet feels endless up to its real limit.
  const tableScrollRef = useRef(null);
  const ensureFilled = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || !el.clientHeight) return;
    const { grid: g, colWidths: cw } = latestRef.current;
    const rows = g.length;
    const cols = g[0]?.length || 0;
    const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < GROW_MARGIN_PX;
    const wantRows = nearBottom ? Math.min(MAX_ROWS, rows + GROW_ROWS) : rows;
    // All 26 columns from the start: adding a column changes every row (and
    // so redraws them all), whereas adding rows only draws the new ones.
    const wantCols = Math.max(cols, MAX_COLS);
    if (wantRows === rows && wantCols === cols) return;
    const padded = padGrid(g, cw, wantRows, wantCols);
    latestRef.current = { ...latestRef.current, grid: padded.grid, colWidths: padded.colWidths };
    setGrid(padded.grid);
    setColWidths(padded.colWidths);
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => ensureFilled());
    observer.observe(el);
    return () => observer.disconnect();
  }, [ensureFilled]);

  // Re-check after every growth/zoom change - keeps going until the visible
  // area is filled (or the cap is reached).
  const gridRows = grid.length;
  const gridCols = grid[0]?.length || 0;
  useEffect(() => { ensureFilled(); }, [gridRows, gridCols, zoom, ensureFilled]);

  // End a range drag no matter where the mouse is released, and finalize a
  // column resize the same way.
  useEffect(() => {
    const handleUp = () => {
      const wasSelecting = isSelectingRef.current;
      isSelectingRef.current = false;
      pointModeDragRef.current = null;
      if (fillDragRef.current) {
        const src = fillDragRef.current.box;
        fillDragRef.current = null;
        const target = fillTargetRef.current;
        fillTargetRef.current = null;
        setFillTarget(null);
        if (target) latestRef.current.applyFill(src, target);
      }
      const dragSel = dragBoxRef.current;
      dragBoxRef.current = null;
      if (wasSelecting && latestRef.current.paintFormat) latestRef.current.applyPaint(dragSel);
      if (wasSelecting && dragSel && (dragSel.anchor.row !== dragSel.focus.row || dragSel.anchor.col !== dragSel.focus.col)) {
        collapseTextSelection(document.activeElement);
      }
      if (resizeRef.current) {
        const { colIndex } = resizeRef.current;
        const finalWidth = previewWidthRef.current?.colIndex === colIndex ? previewWidthRef.current.width : null;
        resizeRef.current = null;
        previewWidthRef.current = null;
        setPreviewWidth(null);
        if (finalWidth != null) {
          const { grid: latestGrid, colWidths: latestColWidths, emit: latestEmit, pushHistory: latestPushHistory } = latestRef.current;
          latestPushHistory();
          const nextColWidths = latestColWidths.map((w, i) => i === colIndex ? finalWidth : w);
          latestEmit(latestGrid, nextColWidths);
        }
      }
    };
    const handleMove = (e) => {
      if (!resizeRef.current) return;
      const { colIndex, startX, startWidth } = resizeRef.current;
      // The table is rendered inside a CSS-zoomed wrapper (see render below),
      // so mouse movement is reported in on-screen (post-zoom) pixels while
      // column widths are stored as real, pre-zoom px - dividing by the
      // current zoom converts the drag back to logical px, so a column
      // doesn't grow/shrink faster or slower than the cursor depending on
      // zoom level.
      const delta = (e.clientX - startX) / latestRef.current.zoom;
      const width = Math.max(MIN_COL_WIDTH, startWidth + delta);
      const next = { colIndex, width };
      previewWidthRef.current = next;
      setPreviewWidth(next);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  const startColumnResize = (e, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { colIndex, startX: e.clientX, startWidth: colWidths[colIndex] || DEFAULT_CELL_WIDTH };
  };

  const widthOf = (ci) => (previewWidth && previewWidth.colIndex === ci ? previewWidth.width : (colWidths[ci] || DEFAULT_CELL_WIDTH));

  // A click on a header takes focus off the cells, and then Cmd+C / Cmd+V
  // wouldn't reach this grid at all - so focus goes to the new active cell.
  const selectFromHeader = (anchor, focus) => {
    setSelection({ anchor, focus });
    cellRefs.current[`${anchor.row}-${anchor.col}`]?.focus({ preventScroll: true });
  };
  const selectColumn = (ci) => selectFromHeader({ row: 0, col: ci }, { row: grid.length - 1, col: ci });
  const selectRow = (ri) => selectFromHeader({ row: ri, col: 0 }, { row: ri, col: (grid[0]?.length || 1) - 1 });
  const selectAll = () => selectFromHeader({ row: 0, col: 0 }, { row: grid.length - 1, col: (grid[0]?.length || 1) - 1 });

  // Shared by the native copy/paste events (Ctrl+C/Ctrl+V, right-click menu)
  // and the toolbar Cut/Copy/Paste buttons below - a button click has no
  // clipboard event to hook, so it goes through navigator.clipboard instead,
  // but the actual selection-to-text and text-to-grid logic is identical.
  const getSelectionTSV = () => {
    const box = normalizeSelection(selection);
    if (!box) return '';
    const tsv = [];
    for (let r = box.minRow; r <= box.maxRow; r++) {
      const rowVals = [];
      for (let c = box.minCol; c <= box.maxCol; c++) rowVals.push(grid[r][c]?.value || '');
      tsv.push(rowVals.join('\t'));
    }
    return tsv.join('\n');
  };

  const normalizeClipText = (text) => String(text || '').replace(/\r/g, '').replace(/\n$/, '');

  const rememberCopy = (isCut) => {
    const box = normalizeSelection(selection);
    if (!box) return '';
    const text = getSelectionTSV();
    const cells = [];
    for (let r = box.minRow; r <= box.maxRow; r++) cells.push(grid[r].slice(box.minCol, box.maxCol + 1).map(c => ({ ...c })));
    clipboardRef.current = { text: normalizeClipText(text), cells, origin: { row: box.minRow, col: box.minCol }, isCut };
    rememberExamCopy(text);
    return text;
  };

  // Pastes cells copied from this same grid: formatting comes too, and
  // formula references move by however far the paste is from where they
  // were copied (unless it was a cut, which moves them unchanged - both as
  // in Excel).
  const pasteCopiedCells = (clip) => {
    const box = normalizeSelection(selection);
    if (!box) return;
    pushHistory();
    const h = clip.cells.length;
    const w = clip.cells[0].length;
    const isSingle = h === 1 && w === 1;
    const isRange = box.minRow !== box.maxRow || box.minCol !== box.maxCol;
    const fillRange = isSingle && isRange;
    const neededRows = Math.min(MAX_ROWS, fillRange ? box.maxRow + 1 : box.minRow + h);
    const neededCols = Math.min(MAX_COLS, fillRange ? box.maxCol + 1 : box.minCol + w);

    let nextGrid = grid.map(row => [...row]);
    const currentCols = nextGrid[0]?.length || DEFAULT_COLS;
    while (nextGrid.length < neededRows) nextGrid.push(Array.from({ length: currentCols }, emptyCell));
    let nextColWidths = colWidths;
    if (currentCols < neededCols) {
      const addCount = neededCols - currentCols;
      nextGrid = nextGrid.map(row => [...row, ...Array.from({ length: addCount }, emptyCell)]);
      nextColWidths = [...colWidths, ...Array.from({ length: addCount }, () => DEFAULT_CELL_WIDTH)];
    }

    const place = (r, c, srcRi, srcCi) => {
      if (r >= nextGrid.length || c >= nextGrid[0].length) return; // past the edge of the sheet
      const source = clip.cells[srcRi][srcCi];
      const dr = clip.isCut ? 0 : r - (clip.origin.row + srcRi);
      const dc = clip.isCut ? 0 : c - (clip.origin.col + srcCi);
      nextGrid[r][c] = withFormat({ ...nextGrid[r][c], value: shiftFormula(source.value || '', dr, dc) }, formatOf(source));
    };
    if (fillRange) {
      for (let r = box.minRow; r <= box.maxRow; r++) for (let c = box.minCol; c <= box.maxCol; c++) place(r, c, 0, 0);
    } else {
      for (let ri = 0; ri < h; ri++) for (let ci = 0; ci < w; ci++) place(box.minRow + ri, box.minCol + ci, ri, ci);
    }
    if (clip.isCut) clipboardRef.current = null; // a cut pastes once, like Excel
    emit(nextGrid, nextColWidths);
  };

  const applyPastedText = (text) => {
    const box = normalizeSelection(selection);
    if (!box || !text) return;
    if (clipboardRef.current && clipboardRef.current.text === normalizeClipText(text)) {
      pasteCopiedCells(clipboardRef.current);
      return;
    }
    pushHistory();

    const lines = text.replace(/\r/g, '').split('\n');
    if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop(); // drop the trailing blank line a copy leaves
    const rowsOfValues = lines.map(line => line.split('\t'));
    const isSingleValue = rowsOfValues.length === 1 && rowsOfValues[0].length === 1;
    const isRangeSelection = box.minRow !== box.maxRow || box.minCol !== box.maxCol;

    // A single copied value pasted onto a range fills the whole range
    // (matches Excel/Sheets); otherwise the block is pasted starting at the
    // active cell, growing the grid if it doesn't already reach that far.
    const neededRows = Math.min(MAX_ROWS, isSingleValue && isRangeSelection ? box.maxRow + 1 : box.minRow + rowsOfValues.length);
    const neededCols = Math.min(MAX_COLS, isSingleValue && isRangeSelection
      ? box.maxCol + 1
      : box.minCol + Math.max(...rowsOfValues.map(r => r.length)));

    let nextGrid = grid.map(row => row.map(cell => ({ ...cell })));
    const currentCols = nextGrid[0]?.length || DEFAULT_COLS;
    while (nextGrid.length < neededRows) nextGrid.push(Array.from({ length: currentCols }, emptyCell));

    let nextColWidths = colWidths;
    if (currentCols < neededCols) {
      const addCount = neededCols - currentCols;
      nextGrid = nextGrid.map(row => [...row, ...Array.from({ length: addCount }, emptyCell)]);
      nextColWidths = [...colWidths, ...Array.from({ length: addCount }, () => DEFAULT_CELL_WIDTH)];
    }

    if (isSingleValue && isRangeSelection) {
      for (let r = box.minRow; r <= box.maxRow; r++) {
        for (let c = box.minCol; c <= box.maxCol; c++) nextGrid[r][c] = { ...nextGrid[r][c], value: rowsOfValues[0][0] };
      }
    } else {
      rowsOfValues.forEach((rowVals, ri) => {
        rowVals.forEach((val, ci) => {
          const r = box.minRow + ri, c = box.minCol + ci;
          if (r >= nextGrid.length || c >= nextGrid[0].length) return; // past the edge of the sheet
          nextGrid[r][c] = { ...nextGrid[r][c], value: val };
        });
      });
    }

    emit(nextGrid, nextColWidths);
  };

  // Selecting some of a single cell's text and copying copies just that text,
  // as normal. Otherwise the whole cell/range is copied, like Excel - with
  // several cells selected, the range always wins over any selected text.
  const hasTextSelection = (e) => {
    const current = normalizeSelection(selection);
    if (current && (current.minRow !== current.maxRow || current.minCol !== current.maxCol)) return false;
    const el = e.target;
    return el && el.tagName === 'INPUT' && el.selectionStart !== el.selectionEnd;
  };

  const handleCopy = (e) => {
    if (!normalizeSelection(selection) || hasTextSelection(e)) return;
    e.clipboardData.setData('text/plain', rememberCopy(false));
    e.preventDefault();
  };

  const handleCut = (e) => {
    if (disabled || !normalizeSelection(selection) || hasTextSelection(e)) return;
    e.clipboardData.setData('text/plain', rememberCopy(true));
    e.preventDefault();
    clearSelectionValues();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    applyPastedText(text);
  };

  // Toolbar Cut/Copy/Paste buttons - go through navigator.clipboard since a
  // button click has no native copy/paste event to hook into. A click is a
  // genuine user gesture, so this is allowed even in Safari, unlike a
  // script-triggered clipboard read with no user interaction behind it.
  const handleCopyButton = async () => {
    try { await navigator.clipboard.writeText(rememberCopy(false)); }
    catch (err) { console.error('Copy failed:', err); }
  };

  const handleCutButton = async () => {
    try {
      await navigator.clipboard.writeText(rememberCopy(true));
      clearSelectionValues();
    } catch (err) { console.error('Cut failed:', err); }
  };

  const handlePasteButton = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!isExamCopy(text)) { alert(PASTE_BLOCKED_MESSAGE); return; }
      applyPastedText(text);
    } catch (err) {
      alert("Couldn't read from the clipboard - try Ctrl+V (or Cmd+V) instead, or check this site has clipboard permission in your browser.");
    }
  };

  // The browser's own right-click menu can't copy a range of cells (its Copy
  // only sees text selected in one input), so the grid has its own.
  const handleCellContextMenu = (e) => {
    if (keepBrowserMenuRef.current) { keepBrowserMenuRef.current = false; return; }
    e.preventDefault();
    // Chrome on a Mac selects the word under a right-click
    collapseTextSelection(e.target);
    collapseTextSelection(document.activeElement);
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - CONTEXT_MENU_WIDTH - 8),
      y: Math.min(e.clientY, window.innerHeight - CONTEXT_MENU_HEIGHT - 8)
    });
  };

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onDown = (e) => { if (!e.target.closest?.('[data-grid-menu]')) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  const box = normalizeSelection(selection);
  const active = grid[selection.anchor.row]?.[selection.anchor.col];
  // The range the formula being typed is pointing at (by click, drag or
  // arrow keys), outlined with a dashed border like Excel's - only while
  // that reference is still the last thing in the formula.
  const lastPoint = lastPointRef.current;
  const pointBox = lastPoint?.pointer && lastPoint.key === `${selection.anchor.row}-${selection.anchor.col}` && lastPoint.value === (active?.value || '')
    ? normalizeSelection(lastPoint.pointer)
    : null;
  const isRangeSelection = !!box && (box.minRow !== box.maxRow || box.minCol !== box.maxCol);
  // Recomputed on every grid change - trivial cost at this grid's size, and
  // far simpler than maintaining a dependency graph for incremental updates.
  const evaluated = useMemo(() => evaluateGrid(grid), [grid]);
  // Column widths with any in-progress drag-resize applied - one array, so
  // GridRow only redraws when a width actually changes.
  const colCount = grid[0]?.length || 0;
  const effectiveWidths = useMemo(
    () => Array.from({ length: colCount }, (_, ci) => (previewWidth && previewWidth.colIndex === ci ? previewWidth.width : (colWidths[ci] || DEFAULT_CELL_WIDTH))),
    [colCount, colWidths, previewWidth]
  );
  // The light tint over a selected range, leaving out the active cell (as
  // Excel does). The active cell is always a corner of the range, so the
  // rest splits into at most two rectangles.
  const selectionTints = () => {
    if (!box || !isRangeSelection) return [];
    const a = selection.anchor;
    const isCorner = (a.row === box.minRow || a.row === box.maxRow) && (a.col === box.minCol || a.col === box.maxCol);
    if (!isCorner) return [box];
    const tints = [];
    const rows = a.row === box.minRow ? [box.minRow + 1, box.maxRow] : [box.minRow, box.maxRow - 1];
    if (rows[0] <= rows[1]) tints.push({ minRow: rows[0], maxRow: rows[1], minCol: box.minCol, maxCol: box.maxCol });
    const cols = a.col === box.minCol ? [box.minCol + 1, box.maxCol] : [box.minCol, box.maxCol - 1];
    if (cols[0] <= cols[1]) tints.push({ minRow: a.row, maxRow: a.row, minCol: cols[0], maxCol: cols[1] });
    return tints;
  };

  // Selection overlay placement. Measured from the real corner cells after
  // each render (not calculated from widths/heights), so it lines up exactly
  // at any zoom, font size or row height. Positioned straight on the DOM
  // nodes - no extra React render.
  const zoomBoxRef = useRef(null);
  const outlineRef = useRef(null);
  const tintRefs = [useRef(null), useRef(null)];
  const fillPreviewRef = useRef(null);
  React.useLayoutEffect(() => {
    const container = zoomBoxRef.current;
    if (!container) return;
    const cr = container.getBoundingClientRect();
    const z = zoom || 1;
    const place = (el, b, grow) => {
      if (!el) return;
      const tdA = b && cellRefs.current[`${b.minRow}-${b.minCol}`]?.parentElement;
      const tdB = b && cellRefs.current[`${b.maxRow}-${b.maxCol}`]?.parentElement;
      if (!tdA || !tdB) { el.style.display = 'none'; return; }
      const ra = tdA.getBoundingClientRect();
      const rb = tdB.getBoundingClientRect();
      const left = (ra.left - cr.left) / z - grow;
      const top = (ra.top - cr.top) / z - grow;
      el.style.display = 'block';
      el.style.transform = `translate(${left}px, ${top}px)`;
      el.style.width = `${(rb.right - ra.left) / z + grow}px`;
      el.style.height = `${(rb.bottom - ra.top) / z + grow}px`;
    };
    const tints = selectionTints();
    place(outlineRef.current, box, 1);
    tintRefs.forEach((ref, i) => place(ref.current, tints[i] || null, 0));
    place(fillPreviewRef.current, fillTarget, 1);
  });

  // Handlers for GridRow, read through a ref so the rows can stay memoised.
  const rowApi = useRef(null);
  rowApi.current = { handleCellMouseDown, handleCellMouseEnter, handleCellContextMenu, updateCell, handleCellKeyDown, selectRow, fillDragRef, cellRefs, box };
  const referenceLabel = isRangeSelection
    ? `${colLetter(box.minCol)}${box.minRow + 1}:${colLetter(box.maxCol)}${box.maxRow + 1}`
    : `${colLetter(selection.anchor.col)}${selection.anchor.row + 1}`;

  // Whether every cell in the current selection has this field on - not
  // just the anchor cell. Drives both the toggle buttons' pressed state
  // below and toggleActive's own on/off decision, so a mixed selection
  // (e.g. only one of several selected cells is bold) shows the button as
  // NOT pressed, matching Excel/Sheets, instead of the anchor cell alone
  // deciding what the whole range's button shows.
  const isFieldActive = (field) => {
    if (!box) return false;
    for (let r = box.minRow; r <= box.maxRow; r++) {
      for (let c = box.minCol; c <= box.maxCol; c++) {
        if (!grid[r][c]?.[field]) return false;
      }
    }
    return true;
  };

  const toggleActive = (field) => {
    if (!box) return;
    const allOn = isFieldActive(field);
    applyToSelection(() => ({ [field]: !allOn }));
  };
  const setAlign = (align) => applyToSelection(() => ({ align }));
  const setFontSize = (fontSize) => applyToSelection(() => ({ fontSize }));
  const setFontColor = (fontColor) => applyToSelection(() => ({ fontColor }));
  const setFillColor = (fillColor) => applyToSelection(() => ({ fillColor }));

  return (
    <div
      onCopy={handleCopy}
      onCut={handleCut}
      onPaste={handlePaste}
      style={{
        height: '100%', display: 'flex', flexDirection: 'column',
        border: '2px solid #dadce0', borderRadius: '8px', overflow: 'hidden', fontFamily: 'Arial, Helvetica, sans-serif'
      }}
    >
      {/* Two-row toolbar, grouped like ACCA's iAssess exam spreadsheet:
          row 1 = editing (clipboard, format painter, undo/redo, zoom),
          row 2 = formatting (font, colours, borders, alignment, numbers). */}
      <div style={{ flexShrink: 0, padding: '4px 8px', background: '#f8f9fa', borderBottom: '1px solid #dadce0' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px', padding: '3px 0' }}>
          <ToolbarButton active={false} disabled={disabled} title="Cut (Ctrl+X)" onClick={handleCutButton}>
            <Scissors size={16} />
          </ToolbarButton>
          <ToolbarButton active={false} disabled={disabled} title="Copy (Ctrl+C)" onClick={handleCopyButton}>
            <Copy size={16} />
          </ToolbarButton>
          <ToolbarButton active={false} disabled={disabled} title="Paste (Ctrl+V)" onClick={handlePasteButton}>
            <ClipboardPaste size={16} />
          </ToolbarButton>
          <ToolbarButton active={!!paintFormat} disabled={disabled} title={paintFormat ? 'Format painter - select the cells to paint (Esc to cancel)' : 'Format painter'} onClick={togglePainter}>
            <Paintbrush size={16} />
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton active={false} disabled={disabled || !canUndo} title="Undo (Ctrl+Z)" onClick={handleUndo}>
            <Undo2 size={16} />
          </ToolbarButton>
          <ToolbarButton active={false} disabled={disabled || !canRedo} title="Redo (Ctrl+Y)" onClick={handleRedo}>
            <Redo2 size={16} />
          </ToolbarButton>
          <ToolbarDivider />
          <select
            value={Math.round(zoom * 100)}
            disabled={disabled}
            onChange={(e) => setZoom(Number(e.target.value) / 100)}
            title="Zoom"
            style={{ border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', color: '#3c4043', padding: '4px 6px', background: 'white', cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {Array.from({ length: Math.round((ZOOM_MAX - ZOOM_MIN) / ZOOM_STEP) + 1 }, (_, i) => Math.round((ZOOM_MIN + i * ZOOM_STEP) * 100)).map(pct => (
              <option key={pct} value={pct}>{pct}%</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px', padding: '3px 0' }}>
          <select
            value={active?.fontSize || 13}
            disabled={disabled}
            onChange={(e) => setFontSize(Number(e.target.value))}
            title="Font size"
            style={{ border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', color: '#3c4043', padding: '4px 6px', background: 'white', cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {[10, 11, 12, 13, 14, 16, 18, 20, 24].map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <ToolbarDivider />
          <ToolbarButton active={isFieldActive('bold')} disabled={disabled} title="Bold" onClick={() => toggleActive('bold')}>
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton active={isFieldActive('italic')} disabled={disabled} title="Italic" onClick={() => toggleActive('italic')}>
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton active={isFieldActive('underline')} disabled={disabled} title="Underline" onClick={() => toggleActive('underline')}>
            <Underline size={16} />
          </ToolbarButton>
          <ColorPickerButton
            icon={Baseline}
            color={active?.fontColor}
            defaultColor="#000000"
            disabled={disabled}
            title="Font color"
            onChange={setFontColor}
          />
          <ColorPickerButton
            icon={PaintBucket}
            color={active?.fillColor}
            defaultColor="#ffffff"
            disabled={disabled}
            title="Cell fill color"
            onChange={setFillColor}
            allowClear
            clearTitle="No fill"
          />
          <MenuButton icon={Grid2x2} title="Borders" items={BORDER_PRESETS} disabled={disabled} onSelect={applyBorderPreset} />
          <ToolbarDivider />
          <ToolbarButton active={active?.align === 'left'} disabled={disabled} title="Align left" onClick={() => setAlign('left')}>
            <AlignLeft size={16} />
          </ToolbarButton>
          <ToolbarButton active={active?.align === 'center'} disabled={disabled} title="Align center" onClick={() => setAlign('center')}>
            <AlignCenter size={16} />
          </ToolbarButton>
          <ToolbarButton active={active?.align === 'right'} disabled={disabled} title="Align right" onClick={() => setAlign('right')}>
            <AlignRight size={16} />
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton active={false} disabled={disabled} title="Decrease decimal places" onClick={() => changeDecimals(-1)}>
            <DecimalsArrowLeft size={16} />
          </ToolbarButton>
          <ToolbarButton active={false} disabled={disabled} title="Increase decimal places" onClick={() => changeDecimals(1)}>
            <DecimalsArrowRight size={16} />
          </ToolbarButton>
          <ToolbarButton active={false} disabled={disabled} title="Percentage format" onClick={() => applyNumberFormat('percent0')}>
            <Percent size={16} />
          </ToolbarButton>
          <MenuButton icon={Hash} title="Number format" items={NUMBER_FORMATS} disabled={disabled} onSelect={applyNumberFormat} />
          <MenuButton icon={Coins} title="Insert currency symbol" items={CURRENCY_SYMBOLS} disabled={disabled} onSelect={insertSymbol} />
        </div>
      </div>

      {/* Name box + formula bar, mirroring the active cell's value */}
      <div style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0, borderBottom: '1px solid #dadce0' }}>
        <div style={{
          width: '76px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: '500', color: '#3c4043', background: '#f8f9fa',
          borderRight: '1px solid #dadce0'
        }}>
          {referenceLabel}
        </div>
        <div
          title="Start a cell with = to enter a formula, e.g. =SUM(A1:A5). Supported: SUM, AVERAGE, ROUND, POWER (or ^), SQRT, COUNT, LOG, LN, EXP, NPV, IRR, MIRR, NORMSDIST, and +-*/ with cell references ($A$1 for a fixed reference)."
          style={{
            width: '28px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontStyle: 'italic', color: '#5f6368', borderRight: '1px solid #dadce0', cursor: 'help'
          }}
        >
          fx
        </div>
        <input
          type="text"
          ref={formulaBarRef}
          value={active?.value ?? ''}
          disabled={disabled}
          placeholder="Type a value, or =SUM(A1:A5) for a formula"
          onChange={(e) => updateCell(selection.anchor.row, selection.anchor.col, { value: e.target.value })}
          onKeyDown={handleFormulaBarKeyDown}
          style={{
            flex: 1, padding: '6px 10px', border: 'none', outline: 'none', fontSize: '13px',
            fontFamily: 'inherit',
            fontWeight: active?.bold ? '700' : '400',
            fontStyle: active?.italic ? 'italic' : 'normal'
          }}
        />
      </div>

      <div ref={tableScrollRef} onScroll={ensureFilled} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* zoom (not transform:scale) - it doesn't create a new containing
            block, so the header row/column's position:sticky below still
            sticks to this scroll container exactly as before. Applied here,
            one level in from tableScrollRef, so the ResizeObserver watching
            tableScrollRef's own box (the auto-column-fill effect above)
            keeps seeing the true, unzoomed available width. */}
        <div ref={zoomBoxRef} style={{ zoom, width: 'fit-content', position: 'relative' }}>
        <table style={{
          borderCollapse: 'collapse', tableLayout: 'fixed',
          // table-layout:fixed with no explicit width treats column widths
          // as proportions of whatever width the table ends up at, and
          // shrinks to fit its container - so a narrower window squeezes
          // every column instead of leaving them alone. Pinning the table's
          // own width to the true sum of column widths stops that: the
          // table simply doesn't fit, and the scroll container above
          // scrolls horizontally instead, exactly like a real spreadsheet.
          width: `${ROW_HEADER_WIDTH + (grid[0] || []).reduce((sum, _, ci) => sum + widthOf(ci), 0)}px`
        }}>
          <colgroup>
            <col style={{ width: `${ROW_HEADER_WIDTH}px` }} />
            {(grid[0] || []).map((_, ci) => <col key={ci} style={{ width: `${widthOf(ci)}px` }} />)}
          </colgroup>
          <thead>
            <tr>
              <th
                onClick={selectAll}
                title="Select all"
                style={{
                  position: 'sticky', top: 0, left: 0, zIndex: 5,
                  width: `${ROW_HEADER_WIDTH}px`, height: `${ROW_HEIGHT}px`,
                  background: '#f1f3f4', border: '1px solid #dadce0', cursor: 'pointer'
                }}
              />
              {(grid[0] || []).map((_, ci) => (
                <th
                  key={ci}
                  onClick={() => selectColumn(ci)}
                  style={{
                    position: 'sticky', top: 0, zIndex: 4,
                    width: `${widthOf(ci)}px`, height: `${ROW_HEIGHT}px`,
                    background: box && ci >= box.minCol && ci <= box.maxCol ? '#d2e3fc' : '#f1f3f4',
                    color: box && ci >= box.minCol && ci <= box.maxCol ? SELECTION_COLOR : '#5f6368',
                    border: '1px solid #dadce0',
                    fontSize: '12px', fontWeight: '500', cursor: 'pointer', userSelect: 'none'
                  }}
                >
                  {colLetter(ci)}
                  <div
                    onMouseDown={(e) => startColumnResize(e, ci)}
                    title="Drag to resize column"
                    style={{
                      position: 'absolute', top: 0, right: 0, width: '6px', height: `${ROW_HEIGHT}px`,
                      cursor: 'col-resize', transform: 'translateX(3px)'
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, ri) => {
              const inBoxRow = !!box && ri >= box.minRow && ri <= box.maxRow;
              const inPointRow = !!pointBox && ri >= pointBox.minRow && ri <= pointBox.maxRow;
              return (
                <GridRow
                  key={ri}
                  ri={ri}
                  row={row}
                  evalRow={evaluated[ri]}
                  widths={effectiveWidths}
                  disabled={disabled}
                  api={rowApi}
                  inBoxRow={inBoxRow}
                  anchorCol={ri === selection.anchor.row ? selection.anchor.col : -1}
                  pointMinCol={inPointRow ? pointBox.minCol : -1}
                  pointMaxCol={inPointRow ? pointBox.maxCol : -1}
                  pointTop={inPointRow && ri === pointBox.minRow}
                  pointBottom={inPointRow && ri === pointBox.maxRow}
                />
              );
            })}
          </tbody>
        </table>
        {/* Selection overlay: one floating outline over the grid, on its own
            compositing layer so moving it doesn't repaint the cells, plus
            the range tint, the fill handle and the fill-drag preview - drawn
            the way Excel/Sheets do it. Placed by the layout effect above.
            Clicks pass through to the cells (pointer-events:none), except
            on the fill handle itself. */}
        {tintRefs.map((ref, i) => (
          <div key={i} ref={ref} style={{
            display: 'none', position: 'absolute', left: 0, top: 0,
            background: 'rgba(26,115,232,0.12)', pointerEvents: 'none', zIndex: 2
          }} />
        ))}
        <div ref={outlineRef} style={{
          display: 'none', position: 'absolute', left: 0, top: 0, willChange: 'transform',
          boxSizing: 'border-box', border: `2px solid ${SELECTION_COLOR}`, pointerEvents: 'none', zIndex: 2
        }}>
          {!disabled && (
            <div
              title="Drag to fill"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fillDragRef.current = { box };
              }}
              style={{
                position: 'absolute', right: '-5px', bottom: '-5px', width: '7px', height: '7px',
                background: SELECTION_COLOR, border: '1px solid white', cursor: 'crosshair', pointerEvents: 'auto'
              }}
            />
          )}
        </div>
        <div ref={fillPreviewRef} style={{
          display: 'none', position: 'absolute', left: 0, top: 0,
          boxSizing: 'border-box', border: '2px dashed #5f6368', background: 'rgba(95,99,104,0.08)',
          pointerEvents: 'none', zIndex: 2
        }} />
        </div>
      </div>

      {contextMenu && (
        <div
          data-grid-menu
          onMouseDown={(e) => e.preventDefault()} // keep focus on the active cell
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 1000,
            width: `${CONTEXT_MENU_WIDTH}px`, padding: '4px 0', background: 'white',
            border: '1px solid #dadce0', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.18)'
          }}
        >
          {[
            { label: 'Cut', keyName: 'X', Icon: Scissors, run: handleCutButton, off: disabled },
            { label: 'Copy', keyName: 'C', Icon: Copy, run: handleCopyButton, off: false },
            { label: 'Paste', keyName: 'V', Icon: ClipboardPaste, run: handlePasteButton, off: disabled }
          ].map(({ label, keyName, Icon, run, off }) => (
            <button
              key={label}
              type="button"
              disabled={off}
              onClick={() => { setContextMenu(null); run(); }}
              onMouseEnter={(e) => { if (!off) e.currentTarget.style.background = '#f1f3f4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%', height: '30px', padding: '0 12px',
                border: 'none', background: 'transparent', fontSize: '13px', textAlign: 'left',
                color: off ? '#9aa0a6' : '#202124', cursor: off ? 'default' : 'pointer'
              }}
            >
              <Icon size={14} />
              <span style={{ flex: 1 }}>{label}</span>
              <span style={{ color: '#80868b', fontSize: '12px' }}>{IS_MAC ? 'Cmd' : 'Ctrl'}+{keyName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExamSpreadsheetGrid;
